import UtilsService from '../services/UtilsService';
import { ContentType, LessonMode, QuestionFeedbackState, questionFeedbackFromScores } from '../../Constants';
import { Behavior } from './Behavior';
import questionFeedbackManager from '../managers/QuestionFeedbackManager';
import lessonManager from '../managers/LessonManager';
import responseManager from '../managers/ResponseManager';
import responseService from '../services/ResponseService';
import Auth from '../services/AuthService';
import studentActivityManager from '../managers/StudentActivityManager';

export class LongTextBehavior extends Behavior {
  checkForValidResponse = (responseAnswer, model) => {
    // Valid if the response exists, is not empty, and is not the default value
    // TODO: Add the model to the parameters here so we can check for the default value. The other solution would be to include the default value in the response.
    return (responseAnswer.text !== null && UtilsService.stripTagsAndEntities(responseAnswer.text).trim() !== '' &&
    UtilsService.stripTagsAndEntities(responseAnswer.text).trim() !== UtilsService.stripTagsAndEntities(model.defaultText).trim())||model.offlineGrading;
  }

  setCorrectAnswer = (state, model) => {
  }

  resetStudentAnswer = (state) => {
  }

  setResponseData = (data, responseAnswer, model) => {
    // No dependence on the model. Text is stored in data by ckeditor
    responseAnswer.text = data;
  }

  getInitializedResponse = (lessonElementId, model) => {
    const responseAnswer = {
      text: model.defaultText || '',

      lessonElementId
    };
    return responseAnswer;
  }

  isAutoScored = (model) => {
    return (ContentType.getAutoscore(model.type) &&
      !model.survey && !model.unscored);
  }

  getAnswerFeedback = (id, responseAnswer) => {
    // never used longText is not autoscored

  }

  getCorrectAnswersText = (model) => {
    // There is no correct answer for this question type.
    return '';
  }

  getQuestionFeedbackText = (model, correctFeedback, incorrectFeedback, correctFeedbackBody, incorrectFeedbackBody, mode, questionIsCorrect, currentAttemptCount, { lessonElementState }) => {
    let textBody = '';
    if (mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.STUDENT_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) {
      textBody += UtilsService.previewWarning();
    } else {
      if (typeof currentAttemptCount !== 'undefined' && currentAttemptCount !== -1 && currentAttemptCount > 1) {
        textBody += UtilsService.saveWarning();
      } else {
        textBody += UtilsService.saveCommit();
      }
    }

    let textHeader = '';
    let attemptsText = '';
    
    // Add attempts remaining text for practice mode when answer is incorrect or partially correct
    if (questionFeedbackManager.isPractice && 
        (questionIsCorrect === QuestionFeedbackState.INCORRECT || questionIsCorrect === QuestionFeedbackState.PARTIAL_CORRECT) &&
        model.lessonElementId) {
      attemptsText = questionFeedbackManager.getAttemptsRemainingText(model.lessonElementId);
    }

    // Add attempts text after the header if it exists
    if (attemptsText) {
      textHeader += attemptsText;
    }

    let finalText = '<div class=\'question-feedback-header\'>';
    finalText += textHeader;
    finalText += '</div>';
    finalText += '<div class=\'question-feedback-body\'>';
    finalText += textBody;
    finalText += '</div>';

    return finalText;
  }

  isAnswerCorrect = (answerId, responseAnswer) => {
    // manual scored
    return false;
  }

  isQuestionCorrect = (lessonElementState, lessonElementId) => {
    if (!lessonElementState) {
      return QuestionFeedbackState.INCORRECT;
    }

    return questionFeedbackFromScores(lessonElementState.submittedItemScore, lessonElementState.maxScore);
  }

  // Response dependency methods
  hasResponseDependency = (model, lessonElementId) => {
    const responseDependency = lessonManager.lessonOptions?.responseDependency;

    const responseDependencyValue = responseDependency && responseDependency[lessonElementId];
    
    if (!responseDependencyValue) {
      return false;
    }
    
    // Handle new format where value is an object
    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        return responseDependencyValue.questionId;
      } else if (responseDependencyValue.source === 'differentLesson') {
        // For differentLesson, check if lessonId and questionForeverId are not null/undefined/empty
        return responseDependencyValue.lessonId && 
               responseDependencyValue.questionForeverId &&
               responseDependencyValue.lessonId !== '' &&
               responseDependencyValue.questionForeverId !== '';
      }
    }
    
    // Handle old format where value is a string (questionId)
    return responseDependencyValue !== null && responseDependencyValue !== '';
  }

  getPreviousResponse = (model, lessonElementId) => {
    if (!this.hasResponseDependency(model, lessonElementId)) {
      return null;
    }

    const responseDependency = lessonManager.lessonOptions?.responseDependency;
    const responseDependencyValue = responseDependency[lessonElementId];
    let previousQuestionId;
    
    // Handle new format where value is an object
    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        previousQuestionId = responseDependencyValue.questionId;
      } else if (responseDependencyValue.source === 'differentLesson') {
        // For differentLesson source, call API to get prior responses
        this.fetchPriorResponsesFromDifferentLesson(responseDependencyValue, lessonElementId);
        return null; // Return null for now, will be handled by the API call
      } else {
        return null;
      }
    } else {
      // Handle old format where value is a string (questionId)
      previousQuestionId = responseDependencyValue;
    }
    
    const previousQuestionState = responseManager.getLessonElementState(previousQuestionId);
    
    if (!previousQuestionState || !previousQuestionState.currentResponseAnswer) {
      return null;
    }

    return previousQuestionState.currentResponseAnswer.text || '';
  }

  fetchPriorResponsesFromDifferentLesson = async (responseDependencyValue, lessonElementId) => {
    try {
      const { lessonId, questionForeverId } = responseDependencyValue;
      const activityId = studentActivityManager.studentActivityId;
      
      if (!activityId) {
        console.warn('No activity ID available for fetching prior responses');
        return;
      }

      const response = await Auth.fetch(`${Auth.ecms}/api/getPriorResponses?lessonForeverId=${lessonId}&questionForeverId=${questionForeverId}&activityId=${activityId}`, {
        method: 'GET',
      });

      console.log('Prior responses from different lesson:', response);
      
      if (response && response.responses && Array.isArray(response.responses)) {
        const responses = response.responses;
        
        // Filter out empty responses
        const nonEmptyResponses = responses.filter(resp => {
          try {
            const content = JSON.parse(resp.content);
            return content.text && UtilsService.stripTagsAndEntities(content.text).trim() !== '';
          } catch (e) {
            return false;
          }
        });
        
        if (nonEmptyResponses.length === 0) {
          // Show empty response warning
          responseService.alertEmptyPreviousResponse();
          return;
        }
        
        if (nonEmptyResponses.length === 1) {
          // Single response - populate directly
          const singleResponse = nonEmptyResponses[0];
          try {
            const content = JSON.parse(singleResponse.content);
            const lessonElementState = responseManager.getLessonElementState(lessonElementId);
            if (lessonElementState) {
              lessonElementState.setCurrentResponse({
                ...lessonElementState.currentResponseAnswer,
                text: content.text
              });
            }
          } catch (e) {
            console.error('Error parsing single response content:', e);
            responseService.alertEmptyPreviousResponse();
          }
        } else {
          // Multiple responses - show selection dialog
          this.showResponseSelectionDialog(nonEmptyResponses, lessonElementId);
        }
      } else {
        responseService.alertEmptyPreviousResponse();
      }
    } catch (error) {
      console.error('Error fetching prior responses from different lesson:', error);
      responseService.alertEmptyPreviousResponse();
    }
  }

  populateWithPreviousResponse = (lessonElementId, model) => {
    const previousResponse = this.getPreviousResponse(model, lessonElementId);
    if (previousResponse && previousResponse.trim() !== '') {
      const lessonElementState = responseManager.getLessonElementState(lessonElementId);
      if (lessonElementState) {
        lessonElementState.setCurrentResponse({
          ...lessonElementState.currentResponseAnswer,
          text: previousResponse
        });
      }
    } else {
      // Show alert if previous response is empty
      responseService.alertEmptyPreviousResponse();
    }
  }

  hasCurrentContent = (lessonElementId) => {
    const lessonElementState = responseManager.getLessonElementState(lessonElementId);
    if (!lessonElementState || !lessonElementState.currentResponseAnswer) {
      return false;
    }
    
    const currentText = lessonElementState.currentResponseAnswer.text;
    return currentText && UtilsService.stripTagsAndEntities(currentText).trim() !== '';
  }

  showResponseSelectionDialog = async (responses, lessonElementId) => {
    try {
      const { showResponseSelectionDialog } = await import('../components/dialogs/ResponseSelectionDialog');
      
      const responseData = responses.map((resp, index) => {
        try {
          const content = JSON.parse(resp.content);
          return {
            id: resp.id,
            index: index + 1,
            content: content,
            creationDate: resp.creationTimestamp,
            modificationDate: resp.modificationTimestamp,
            activityName: resp.activityName
          };
        } catch (e) {
          return null;
        }
      }).filter(Boolean);

      const selectedResponse = await showResponseSelectionDialog({
        responses: responseData,
        type: 'longtext'
      });

      if (selectedResponse) {
        const lessonElementState = responseManager.getLessonElementState(lessonElementId);
        if (lessonElementState) {
          lessonElementState.setCurrentResponse({
            ...lessonElementState.currentResponseAnswer,
            text: selectedResponse.content.text
          });
        }
      }
    } catch (error) {
      console.error('Error showing response selection dialog:', error);
    }
  }
}
export default new LongTextBehavior();
